import uiConfig from 'src/lib/ui/config';
import { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import { createEmitter, createPersistentEmitter } from 'src/lib/Emitter';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import i18n from 'src/lib/i18n/i18n';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { shouldShowFooter } from 'src/redux/getters/ui';
import animate from '@play-co/timestep-core/lib/animate';
import { animDuration, getScreenTop } from 'src/lib/utils';
import CareMenu from './CareMenu';
import MainMenu from './MainMenu';
import LockedButtonOverlay from './LockedButtonOverlay';
import StateObserver from '../../StateObserver';
import sounds from '../../lib/sounds';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { Pet } from 'src/replicant/buruburu/pet';
import { AvailableDisabledFeatures } from '../popups/PopupFeatureDisabled';
import FriendsMenu from './FriendsMenu';
import { Conversation } from 'src/replicant/conversation';
import { Social } from 'src/replicant/social';
import statePromise from '../../lib/statePromise';
import ShopMenu from './ShopMenu';
import { openProfile } from '../profile/profile.sequence';
import { Minigame } from 'src/replicant/minigame';
import PopupMonitor from '../logic/PopupMonitor';
import { startMinigame } from '../minigames/minigames.sequence';
import { getTutorialController } from '../tutorial/TutorialController';
import platform from '@play-co/gcinstant';
import { getDynamicTestBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';
import { VisitRecord } from 'src/replicant/visit/record';

export default class Footer extends FlexBoxView {
  constructor({ superview }: Pick<ViewOpts, 'superview'>) {
    super({
      superview,
      ...uiConfig.footer.container,
    });

    this.addSubview(new FooterMenuButton());
    /*
    const { user } = StateObserver.getState();
    const isFriendMenuLocked = !Social.getters(user).isFriendInviteAvailable();
    const friendButton = this.addSubview(new FooterFriendsButton());
    if (isFriendMenuLocked) {
      friendButton.setLocked(true);
      statePromise(({ user }) =>
        Social.getters(user).isFriendInviteAvailable(),
      ).then(() => friendButton.setLocked(false));
    }
    */

    const careButton = this.addSubview(new FooterCareButton());
    const minigamesButton = this.addSubview(new MinigameButton());

    //Coins energy shop
    const shopButton = this.addSubview(new ShopMenuButton());

    // anchor elements
    createEmitter(this, ({ ui }) => ui.screenSize).addListener((screen) => {
      this.updateOpts({
        y: getScreenTop() + screen.height - this.style.height,
      });
    });

    // Disable care/minigame buttons while asleep, or if the player hasn't unlocked them.
    createEmitter(this, ({ user, ui }) => ({
      isAsleep: Pet.getters(user).hasStatusEffect(
        'asleep',
        StateObserver.now(),
      ),
      hasOpenedDialog: Conversation.getters(user).hasOpenedDialog(),
      areMinigamesAvailable: Minigame.getters(user).areMinigamesAvailable(),
      isVisitingFriend: ui.transition.nextScene === 'friendsLivingRoom',
      isDead:
        Pet.getters(user).getPetState(StateObserver.now(), false) === 'dead',
    })).addListener(
      ({
        isAsleep,
        hasOpenedDialog,
        areMinigamesAvailable,
        isVisitingFriend,
        isDead,
      }) => {
        // friendButton.setLocked(isVisitingFriend, 'visitingFriend');
        shopButton.setLocked(isVisitingFriend, 'visitingFriend');
        minigamesButton.setLocked(isVisitingFriend, 'visitingFriend');

        if (isAsleep) {
          careButton.setActive(false);
          careButton.setLocked(true, 'sleeping');
          minigamesButton.setLocked(true, 'sleeping');
        } else {
          careButton.setLocked(!hasOpenedDialog);

          if (!isVisitingFriend) {
            minigamesButton.setLocked(!areMinigamesAvailable);
          }
        }

        if (isDead) {
          minigamesButton.setLocked(true, 'dead');
          careButton.setLocked(true, 'dead');
        }
      },
    );

    createPersistentEmitter(shouldShowFooter).addListener((showFooter) => {
      if (showFooter) {
        this.fadeIn();
      } else {
        this.fadeOut();
      }
    });
  }

  private fadeIn() {
    animate(this)
      .clear()
      .wait(animDuration * 4)
      .now(() =>
        this.updateOpts({
          visible: true,
          opacity: this.style.opacity,
          x: -100,
        }),
      )
      .then(
        { opacity: this.style.opacity, x: -100 },
        animDuration,
        animate.easeOut,
      )
      .then({ opacity: 1, x: 0 }, animDuration, animate.easeOut);
  }

  private fadeOut() {
    animate(this)
      .clear()
      .wait(0)
      .now({ opacity: this.style.opacity, x: this.style.x }, 0, animate.easeOut)
      .then(
        { opacity: this.style.opacity, x: this.style.x },
        animDuration * 0.5,
        animate.easeOut,
      )
      .then({ opacity: 0, x: -100 }, animDuration, animate.easeOut)
      .then(() => {
        this.style.visible = false;
      });
  }
}

export type FooterButtonType =
  | 'menu'
  | 'visit'
  | 'friends'
  | 'care'
  | 'game'
  | 'shop';
type AvailableDisabledFooterButtons = Extract<
  AvailableDisabledFeatures,
  FooterButtonType
>;
type FooterButtonOpts = { type: FooterButtonType };

class FooterButton extends ButtonScaleViewWithText {
  isActive = false;

  constructor(protected opts: FooterButtonOpts) {
    super({
      ...uiConfig.footer.button,
      icon: uiConfig.footer.actionIcons[opts.type],
      localeText: () => i18n(`footer.${opts.type}`),
      tracking: { feature: 'footer', $subFeature: opts.type },
    });

    this.icon.updateOpts({ scaleMethod: 'contain' });
  }

  async onClick() {
    if (this.isActive) {
      this.setActive(false);
      sounds.sfx.play('cancel');
    } else {
      // Do not show menu if conversation is not finished
      if (StateObserver.getState().conversation[platform.playerID].activeDialog)
        return;
      this.setActive(true);
      sounds.sfx.play('ok');
    }
  }

  setActive(isActive: boolean) {
    if (isActive === this.isActive) {
      return;
    }

    const { button, buttonActive, actionIcons } = uiConfig.footer;
    const icon = actionIcons[this.opts.type];
    const iconActive = actionIcons[`${this.opts.type}Active`];
    let buttonHeight = button.height;
    let iconHeight = icon.height;
    if (isActive) {
      buttonHeight += 25;
      iconHeight += 25;
      this.style.zIndex = 1;
      this.updateButtonImage(buttonActive.image);
      this.icon.setImage(iconActive.image);
    } else {
      this.updateButtonImage(button.image);
      this.icon.setImage(icon.image);
    }

    animate(this)
      .clear()
      .now(
        {
          height: buttonHeight,
          offsetY: button.height - buttonHeight,
        },
        animDuration,
        animate.easeOut,
      )
      .then(() => {
        if (!this.isActive) {
          this.style.zIndex = 0;
        }
      });
    animate(this.icon)
      .clear()
      .now({ height: iconHeight }, animDuration, animate.easeOut);
    animate(this.label)
      .clear()
      .now(
        { offsetY: iconHeight - icon.height },
        animDuration,
        animate.easeOut,
      );

    this.isActive = isActive;
  }
}

class LockableFooterButton<
  T extends AvailableDisabledFooterButtons = AvailableDisabledFooterButtons
> extends FooterButton {
  private isLocked: boolean;
  private lockedReason?: 'sleeping' | 'visitingFriend' | 'dead';
  private lockedButtonOverlay;

  constructor(opts: { type: T; isLocked?: boolean }) {
    super(opts);
    this.setLocked(opts.isLocked ?? false);
  }

  setLocked(
    isLocked: boolean,
    reason?: 'sleeping' | 'visitingFriend' | 'dead',
  ) {
    this.lockedReason = reason;
    if (this.isLocked === isLocked) {
      return;
    }

    this.isLocked = isLocked;
    if (isLocked) {
      const { width, height } = this.style;
      this.lockedButtonOverlay = this.addSubview(
        new LockedButtonOverlay({ width, height }),
      );
    } else if (this.lockedButtonOverlay) {
      const overlay = this.lockedButtonOverlay;
      animate(overlay)
        .commit()
        .now({ opacity: 0 }, 250, animate.linear)
        .then(() => overlay.removeFromSuperview());
      this.lockedButtonOverlay = null;
    }
  }

  async onClick() {
    // Show feature disabled popup
    if (this.isLocked) {
      sounds.sfx.play('ok');

      await openPopupPromise('popupFeatureDisabled', {
        feature: this.opts.type as T,
        reason: this.lockedReason,
      });

      sounds.sfx.play('cancel');
    } else {
      // Do not show menu if conversation is not finished
      if (StateObserver.getState().conversation[platform.playerID].activeDialog)
        return;

      return this.handleClick();
    }
  }

  protected async handleClick() {
    return super.onClick();
  }
}

class FooterMenuButton extends FooterButton {
  private mainMenu: MainMenu;

  constructor() {
    super({ type: 'menu' });
  }

  setActive(isActive: boolean) {
    super.setActive(isActive);
    if (isActive) {
      this.mainMenu = new MainMenu();
      this.mainMenu.once('Close', () => this.onClick());
      this.mainMenu.fadeIn(this.getSuperview());
    } else {
      this.mainMenu?.fadeOut();
    }
  }
}

class MinigameButton extends LockableFooterButton {
  constructor() {
    super({
      type: 'game',
      isLocked: !Minigame.getters(
        StateObserver.getState().user,
      ).areMinigamesAvailable(),
    });
  }

  async handleClick() {
    if (!PopupMonitor.isOpen('popupSelectMinigame')) {
      this.setActive(true);
      sounds.sfx.play('ok');
      const selectedMinigame = await openPopupPromise(
        'popupSelectMinigame',
        {},
      );
      this.setActive(false);

      if (selectedMinigame) {
        await startMinigame(selectedMinigame);
      }
    }
  }
}

class ShopMenuButton extends LockableFooterButton {
  private shopMenu: ShopMenu;

  constructor() {
    super({ type: 'shop' });
  }

  setActive(isActive: boolean) {
    super.setActive(isActive);
    if (isActive) {
      this.shopMenu = new ShopMenu();
      this.shopMenu.once('Close', () => this.onClick());
      this.shopMenu.fadeIn(this.getSuperview());
    } else {
      this.shopMenu?.fadeOut();
    }
  }
}

class FooterCareButton extends LockableFooterButton {
  private careMenu: CareMenu;

  constructor() {
    super({ type: 'care' });
  }

  setActive(isActive: boolean) {
    if (isActive && StateObserver.getState().care.currentCareAction) {
      return;
    }

    super.setActive(isActive);
    if (isActive) {
      void getTutorialController().handleEvent('careMenuOpened');
      this.careMenu = new CareMenu();
      this.careMenu.once('Close', () => this.onClick());
      this.careMenu.once('ForceClose', () => this.setActive(false));
      this.careMenu.fadeIn(this.getSuperview());
    } else {
      this.careMenu?.fadeOut();
    }
  }
}

class FooterFriendsButton extends LockableFooterButton {
  private friendsMenu: FriendsMenu;

  constructor() {
    super({ type: 'friends' });
  }

  public async setActive(isActive: boolean) {
    const { user, care } = StateObserver.getState();
    if (isActive && care.currentCareAction) {
      return;
    }

    super.setActive(isActive);

    if (isActive) {
      this.friendsMenu = new FriendsMenu();
      this.friendsMenu.once('Close', () => this.onClick());
      this.friendsMenu.fadeIn(this.getSuperview());

      // Prompt user to setup in-game profile if they haven't already,
      if (user.publicProfile) return;

      const canVisitFriends = VisitRecord.getters.isUnlocked(user);

      // If test is enabled, should only prompt if user can visit friends.
      if (!canVisitFriends) return;

      await openProfile();

      return;
    }

    this.friendsMenu?.fadeOut();
  }
}
